/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState } from "react";
import classNames from "classnames";
import invariant from "invariant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { Link } from "gatsby-plugin-intl";
import _noop from "lodash.noop";
import _debounce from "lodash.debounce";
import PropTypes from "prop-types";

/* Not sure if this still works, since we don't have any SimpleDropdows in the app */
const SimpleDropdown = ({ options = [], onItemClick = _noop, ...props }) => (
  <div
    className="simple-dropdown absolute bg-white w-full py-2 rounded-lg shadow-light"
    {...props}
  >
    {options.map(({ title, key, path }) => (
      <Link
        onClick={event => {
          // prevent only when path is not provided
          if (!path) event.preventDefault();
          onItemClick(key, event);
        }}
        className="text-black hover:text-gray-700 p-2 text-sm inline-block border-b last:border-b-0 w-full"
        to={path}
        key={key}
      >
        {title}
      </Link>
    ))}
  </div>
);

const MegaDropdown = ({ options = [], onItemClick = _noop, ...props }) => (
  <div
    className="mega-dropdown absolute bg-white py-2 rounded-lg shadow-lg flex flex-wrap ltr:text-left rtl:text-right"
    {...props}
  >
    {options.map(
      ({
        title,
        description,
        key,
        path,
        triggersModal,
        component: Component = Link,
      }) => (
        <Component
          key={key}
          serviceKey={key}
          onClick={event => onItemClick(key, event)}
          className="text-black p-2 text-sm inline-block last:border-b-0 w-full sm:w-6/12 md:w-4/12 xl:w-3/12 mb-3 pr-1 pl-1"
          to={path}
          triggersModal={triggersModal}
        >
          <h3 className="text-xl hover:text-primary transition-colors ease-in duration-150">
            {title}
          </h3>
          <p className="mt-2 text-sm leading-6">{description}</p>
        </Component>
      )
    )}
  </div>
);

export const DROPDOWN_TYPES = Object.freeze({
  mega: "MEGA",
  simple: "SIMPLE",
});

export const DROPDOWN_TYPES_COMPONENTS = Object.freeze({
  [DROPDOWN_TYPES.mega]: {
    wrapperClassName: "mega-dropdown-wrapper",
    component: MegaDropdown,
  },
  [DROPDOWN_TYPES.simple]: {
    wrapperClassName: "simple-dropdown-wrapper",
    component: SimpleDropdown,
  },
});

const DropDown = ({ type, children, items = [], className, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);

  const dropdownContent = DROPDOWN_TYPES_COMPONENTS[type];

  const handleMouseLeave = _debounce(() => {
    setIsOpen(false);
  }, 150);

  const handleMouseEnter = () => {
    handleMouseLeave.cancel();
    setIsOpen(true);
  };

  invariant(dropdownContent, `Dropdown type ${type} is not supported`);

  return (
    <>
      <div
        className={classNames(
          "dropdown relative",
          dropdownContent.wrapperClassName,
          isOpen && "is-open"
        )}
        onMouseLeave={handleMouseLeave}
        onMouseEnter={handleMouseEnter}
      >
        <Link
          direction="ltr"
          className={classNames(
            `cursor-pointer flex items-center lg:rtl:flex-row-reverse`,
            className
          )}
          {...props}
        >
          {children}
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`ltr:ml-4 rtl:mr-4 ${isOpen && "transform -rotate-180"}`}
            size="sm"
          />
        </Link>
      </div>

      {isOpen && (
        <dropdownContent.component
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          options={items}
          onItemClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

DropDown.propTypes = {
  type: PropTypes.string,
  style: PropTypes.string,
  className: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.string),
  component: PropTypes.arrayOf(PropTypes.string, PropTypes.node),
};

export default DropDown;
